<template>
  <DesktopOnlyNotice v-if="!isDesktopPointer" title="Open scan alignment on a desktop"
    message="Aligning scans needs a mouse and a larger screen. Please open this on a desktop or laptop." />
  <v-container v-else fluid class="viewer-container d-flex flex-column" style="user-select: none;">
    <div class="viewer-stage flex-grow-1">
      <!-- Full-bleed canvas -->
      <div class="viewer-wrap">
        <div ref="canvasContainer" class="canvas-host" />

        <!-- Download / processing overlay (mirrors the runtime viewer) -->
        <div v-if="loadState.visible" class="model-loading">
          <div v-if="loadState.phase === 'download'" class="ml-inner">
            <v-progress-circular :model-value="downloadPercent ?? 0" :indeterminate="downloadPercent == null"
              :size="96" :width="8" color="#66c7b4">
              <span v-if="downloadPercent != null" class="ml-percent">{{ downloadPercent }}%</span>
            </v-progress-circular>
            <div class="ml-title">Downloading scan</div>
            <div class="ml-sub">{{ loadSubtitle }}</div>
          </div>

          <div v-else class="ml-inner">
            <div class="ml-cube">
              <span class="ml-face ml-front"></span>
              <span class="ml-face ml-back"></span>
              <span class="ml-face ml-right"></span>
              <span class="ml-face ml-left"></span>
              <span class="ml-face ml-top"></span>
              <span class="ml-face ml-bottom"></span>
            </div>
            <div class="ml-title">{{ loadState.total ? 'Preparing scan' : 'Loading scans' }}</div>
            <div class="ml-sub">{{ loadSubtitle }}</div>
          </div>
        </div>

        <div v-else-if="error" class="model-loading">
          <div class="ml-inner">
            <v-icon size="34" color="#f44336">mdi-alert-circle-outline</v-icon>
            <div class="ml-sub">{{ error }}</div>
          </div>
        </div>
      </div>

      <!-- Breadcrumb pill (top-left) — mirrors the runtime viewer (ViewAsset). -->
      <div class="viewer-crumb">
        <nav class="tb-crumbs" aria-label="Breadcrumb">
          <button type="button" class="tb-crumb" @click="goToAssets">Assets</button>
          <v-icon size="14" class="tb-sep">mdi-chevron-right</v-icon>
          <button type="button" class="tb-crumb" @click="goToAdmin">Asset admin</button>
          <v-icon size="14" class="tb-sep">mdi-chevron-right</v-icon>
          <span class="tb-crumb current" :title="version ? version.versionName : 'Model'">{{ version ? version.versionName : 'Model' }}</span>
        </nav>
      </div>

      <!-- Floating right column: all controls (mirrors the runtime viewer's right-hand panel) -->
      <aside class="floating-sidebar sidebar-right">
        <div class="sidebar-content">
          <div class="sidebar-scroll">
          <!-- Scans + default-view action -->
          <section class="sidebar-card">
            <div class="card-header scans-header">
              <div class="card-header-text">
                <div class="card-eyebrow">Composition</div>
                <h3 class="card-title">Scans <span class="title-count">{{ scanItems.length }}</span></h3>
              </div>
              <button type="button" class="hdr-action" :disabled="savingView" title="Save current camera as the default view"
                @click="saveCameraView">
                <v-progress-circular v-if="savingView" indeterminate size="15" width="2" color="#8aa0aa" />
                <v-icon v-else size="18">mdi-camera-marker-outline</v-icon>
                <span>Save view</span>
              </button>
            </div>
            <div class="card-body">
              <v-tooltip location="top" :disabled="addableScans.length > 0"
                text="No more completed scans available.">
                <template #activator="{ props }">
                  <span v-bind="props" class="add-scan-activator">
                    <v-btn class="add-scan-btn" block variant="tonal" prepend-icon="mdi-plus"
                      :disabled="!addableScans.length" @click="openAddDialog">
                      Add scan
                    </v-btn>
                  </span>
                </template>
              </v-tooltip>

              <div class="scan-rows">
                <div v-for="item in scanItems" :key="item.scanId" class="scan-row"
                  :class="{
                    'is-primary': item.isPrimary,
                    'is-selected': item.scanId === selectedScanId,
                    'is-selectable': item.status === 'loaded',
                  }"
                  @click="selectScan(item)">
                  <span class="scan-color-dot" :style="{ backgroundColor: item.pathColor }"
                    title="Camera-path colour"></span>
                  <v-icon size="18" class="scan-icon">mdi-cube-outline</v-icon>
                  <span class="scan-row-name">{{ item.name }}</span>

                  <v-chip v-if="item.isPrimary" size="x-small" color="green" variant="flat" class="ml-1">Primary</v-chip>

                  <span class="scan-status">
                    <v-progress-circular v-if="item.status === 'loading'" indeterminate size="14" width="2" color="#64ffda" />
                    <v-icon v-else-if="item.status === 'loaded'" size="16" color="#4ade80">mdi-check-circle</v-icon>
                    <v-icon v-else-if="item.status === 'error'" size="16" color="#f44336">mdi-alert-circle</v-icon>
                  </span>

                  <v-btn class="icon-btn" icon variant="text" size="x-small"
                    :title="item.visible ? 'Hide scan' : 'Show scan'" @click.stop="toggleVisible(item)">
                    <v-icon size="16">{{ item.visible ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}</v-icon>
                  </v-btn>

                  <v-btn v-if="!item.isPrimary" class="icon-btn remove-btn" icon variant="text" size="x-small"
                    title="Remove scan" @click.stop="onRemoveScan(item)">
                    <v-icon size="16">mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </section>

          <!-- Transform + display (selected supplementary scan) -->
          <section v-if="selectedScanId && !selectedIsPrimary" class="sidebar-card">
            <div class="card-header">
              <div class="card-eyebrow">{{ selectedScanName }}</div>
              <h3 class="card-title">Transform</h3>
            </div>
            <div class="card-body">
              <div class="mode-toggle">
                <button type="button" :class="{ active: gizmoMode === 'translate' }" @click="setGizmoMode('translate')">Move</button>
                <button type="button" :class="{ active: gizmoMode === 'rotate' }" @click="setGizmoMode('rotate')">Rotate</button>
                <button type="button" :class="{ active: gizmoMode === 'scale' }" @click="setGizmoMode('scale')">Scale</button>
              </div>

              <div class="xform-grid">
                <span class="xform-axis xf-x">Pos</span>
                <input type="number" step="0.1" class="xform-input" v-model.number="xform.px"
                  @change="applyTransformFields" @pointerdown="startXformScrub($event, 'px')" />
                <input type="number" step="0.1" class="xform-input" v-model.number="xform.py"
                  @change="applyTransformFields" @pointerdown="startXformScrub($event, 'py')" />
                <input type="number" step="0.1" class="xform-input" v-model.number="xform.pz"
                  @change="applyTransformFields" @pointerdown="startXformScrub($event, 'pz')" />

                <span class="xform-axis">Rot°</span>
                <input type="number" step="1" class="xform-input" v-model.number="xform.rx"
                  @change="applyTransformFields" @pointerdown="startXformScrub($event, 'rx')" />
                <input type="number" step="1" class="xform-input" v-model.number="xform.ry"
                  @change="applyTransformFields" @pointerdown="startXformScrub($event, 'ry')" />
                <input type="number" step="1" class="xform-input" v-model.number="xform.rz"
                  @change="applyTransformFields" @pointerdown="startXformScrub($event, 'rz')" />

                <span class="xform-axis">Scale</span>
                <input type="number" step="0.05" class="xform-input" v-model.number="xform.sx"
                  @change="applyTransformFields" @pointerdown="startXformScrub($event, 'sx')" />
                <input type="number" step="0.05" class="xform-input" v-model.number="xform.sy"
                  @change="applyTransformFields" @pointerdown="startXformScrub($event, 'sy')" />
                <input type="number" step="0.05" class="xform-input" v-model.number="xform.sz"
                  @change="applyTransformFields" @pointerdown="startXformScrub($event, 'sz')" />
              </div>

              <div class="xform-actions">
                <p class="xform-hint"><v-icon size="13">mdi-arrow-left-right</v-icon> Drag a value to adjust · hold Shift for fine · type for exact</p>
                <button type="button" class="xform-reset" title="Reset this scan's transform" @click="resetSelectedTransform">
                  <v-icon size="14">mdi-restore</v-icon><span>Reset</span>
                </button>
              </div>

              <!-- Dimming lives with the transform controls — only meaningful while a scan is selected. -->
              <label class="row-toggle dim-toggle">
                <input type="checkbox" :checked="dimOthers" @change="toggleDimOthers" />
                Dim other scans
              </label>
              <div v-if="dimOthers" class="slider-row">
                <span class="slider-label">Opacity</span>
                <input type="range" min="0.05" max="1" step="0.05" :value="dimOpacity" @input="onDimOpacity" />
                <span class="slider-value">{{ Math.round(dimOpacity * 100) }}%</span>
              </div>
            </div>
          </section>

          <!-- Base scale (primary scan only) — sizes the whole model to real-world units -->
          <section v-if="selectedScanId && selectedIsPrimary" class="sidebar-card">
            <div class="card-header">
              <div class="card-eyebrow">{{ selectedScanName }}</div>
              <h3 class="card-title">Base scale</h3>
            </div>
            <div class="card-body">
              <p class="trim-hint">Uniformly scales the base scan — and the whole model with it — so you
                can size it to real-world units. Display only; it doesn't change the baked point cloud.</p>
              <div class="trim-slider-row">
                <input type="range" min="-3" max="5" step="0.01" :value="primaryScaleLog"
                  @input="onPrimaryScaleChanged(scaleFromLog($event.target.value))" />
                <input type="number" class="scale-number" step="0.01" min="0.001" :value="primaryScale"
                  @change="onPrimaryScaleNumberChange($event)" />
                <span class="scale-unit">×</span>
                <button v-if="primaryScale !== 1" type="button" class="trim-reset"
                  title="Reset to 1× (no scaling)" @click="onPrimaryScaleChanged(1)">
                  <v-icon size="14">mdi-restore</v-icon>
                </button>
              </div>
            </div>
          </section>

          <!-- Trim by camera path (selected scan only) -->
          <section v-if="selectedScanId && selectedScanItem" class="sidebar-card">
            <div class="card-header">
              <div class="card-eyebrow">{{ selectedScanName }}</div>
              <h3 class="card-title">Point reduction</h3>
            </div>
            <div class="card-body">
              <p class="trim-hint">Trim hides points further than the chosen radius from any camera
                position; decimation thins the rest uniformly. Together they preview the baked PLY.</p>

              <div class="trim-row">
                <div class="trim-row-head">
                  <span class="trim-scan-name">Trim by camera path</span>
                  <span v-if="selectedScanItem.helperCount" class="trim-helper-count">
                    {{ selectedScanItem.helperCount }} {{ selectedScanItem.helperCount === 1 ? 'camera' : 'cameras' }}
                  </span>
                  <span v-else class="trim-helper-empty">no cameras</span>
                </div>

                <div v-if="selectedScanItem.helperCount > 0" class="trim-slider-row">
                  <input type="range" min="0" :max="selectedScanItem.trimMax" step="0.05" :value="selectedScanItem.trimRadius"
                    @input="onTrimRadiusChanged(selectedScanItem, $event.target.value)" />
                  <input type="number" class="trim-number" min="0" :max="selectedScanItem.trimMax" step="0.1"
                    :value="Number(selectedScanItem.trimRadius).toFixed(2)"
                    @change="onTrimNumberChange(selectedScanItem, $event)" />
                  <span class="trim-unit">m</span>
                  <button v-if="selectedScanItem.trimRadius > 0" type="button" class="trim-reset"
                    title="Show everything" @click="resetTrimRadius(selectedScanItem)">
                    <v-icon size="14">mdi-restore</v-icon>
                  </button>
                </div>
              </div>

              <div class="trim-row">
                <div class="trim-row-head">
                  <span class="trim-scan-name">
                    Decimation
                    <v-icon size="13" class="trim-info"
                      title="Reduces the number of points to make the model lighter and faster to load. Higher = fewer points, lower detail.">mdi-information-outline</v-icon>
                  </span>
                  <span class="trim-helper-empty">uniform thinning</span>
                </div>

                <div class="trim-slider-row">
                  <input type="range" min="0" max="70" step="5" :value="selectedScanItem.decimateRemoved"
                    @input="onDecimationChanged(selectedScanItem, $event.target.value)" />
                  <input type="number" class="trim-number" min="0" max="70" step="1"
                    :value="selectedScanItem.decimateRemoved"
                    @change="onDecimationNumberChange(selectedScanItem, $event)" />
                  <span class="trim-unit">%</span>
                  <button v-if="selectedScanItem.decimateRemoved > 0" type="button" class="trim-reset"
                    title="Show everything" @click="resetDecimation(selectedScanItem)">
                    <v-icon size="14">mdi-restore</v-icon>
                  </button>
                </div>
              </div>

              <div v-if="selectedScanItem.pointsTotal > 0" class="trim-counts">
                <span class="trim-count-visible">{{ formatCount(selectedScanItem.pointsVisible) }} visible</span>
                <span class="trim-count-hidden">{{ formatCount(selectedScanItem.pointsHidden) }} hidden</span>
                <span class="trim-count-pct">−{{ selectedScanItem.trimReductionPct.toFixed(1) }}%</span>
              </div>
            </div>
          </section>

          </div>

          <div class="sidebar-footer">
            <div class="footer-actions">
              <v-btn class="save-btn" :disabled="!dirty || saving" :loading="saving" @click="saveTransforms">
                {{ dirty ? 'Save changes' : 'Saved' }}
              </v-btn>

              <v-btn class="publish-btn" :disabled="publishDisabled" :loading="publishing || isBaking"
                @click="onPublishClicked">
                {{ publishLabel }}
              </v-btn>
            </div>

            <v-btn v-if="selectedScanId" class="cancel-btn" variant="text" @click="cancelMove">
              Cancel
            </v-btn>
          </div>
        </div>
      </aside>

    </div>

    <v-dialog v-model="isAddDialogVisible" max-width="460">
      <v-card class="add-dialog">
        <v-card-title class="add-dialog-title">Add scans to version</v-card-title>
        <v-card-text class="add-dialog-body">
          <p class="add-dialog-hint">Select one or more completed scans to add to this version.</p>
          <div class="add-scan-list">
            <label v-for="scan in addableScans" :key="scan.id" class="add-scan-option">
              <input type="checkbox" :value="scan.id" v-model="addSelection" />
              <v-icon size="16" class="add-scan-option-icon">mdi-cube-outline</v-icon>
              <span class="add-scan-option-name">{{ scan.name }}</span>
            </label>
          </div>
        </v-card-text>
        <v-card-actions class="add-dialog-actions">
          <v-spacer />
          <button class="ghost-btn" @click="closeAddDialog">Cancel</button>
          <button class="primary-btn" :disabled="!addSelection.length" @click="confirmAddScans">
            Add{{ addSelection.length ? ` (${addSelection.length})` : '' }}
          </button>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmDialog v-model="isPublishConfirmVisible" title="Publish this version?"
      message="This will generate optimised model files for the changed scans. It runs in the background and your original scans are kept. Continue?"
      confirmText="Publish" cancelText="Cancel" icon="mdi-cloud-upload-outline" @confirm="confirmPublish" />

    <v-snackbar v-model="showViewMessage" timeout="2500" color="#1d2b33">{{ viewMessage }}</v-snackbar>
  </v-container>
</template>

<script>
import ViewerEngine from '@/lib/viewer/ViewerEngine';
import { useConfirm } from '@/composable/useConfirm';
import ModelVersionService from '@/services/ModelVersionService';
import AssetService from '@/services/AssetService';
import organizationSasService from '@/services/OrganizationSasService';
import UpdateModelVersionScansModel from '@/domain/modelversion/UpdateModelVersionScansModel';
import UpdateModelVersionModel from '@/domain/modelversion/UpdateModelVersionModel';
import { SCAN_PROCESSING_SUCCESS } from '@/utils/ScanStatus';
import ConfirmDialog from '@/components/dialog/ConfirmDialog.vue';
import DesktopOnlyNotice from '@/components/DesktopOnlyNotice.vue';
import { useOrganizationStore } from '@/store/organizationStore';
import { useDeviceCapability } from '@/composable/useDeviceCapability';
import { dedupeCubemapHelpers, helperPositions, CAMERA_PATH_COLORS } from '@/lib/viewer/pointCloudCull';

export default {
  name: 'ScanAlignmentEditor',
  components: { ConfirmDialog, DesktopOnlyNotice },
  setup() {
    const { isDesktopPointer } = useDeviceCapability();
    return { organizationStore: useOrganizationStore(), isDesktopPointer };
  },
  props: {
    id: { type: String, required: true },        // assetId (route param)
    versionId: { type: String, required: true }, // model version id (route param)
  },
  data() {
    return {
      version: null,
      // { scanId, name, isPrimary, status: 'loading'|'loaded'|'error', visible, modelPath, transform }
      scanItems: [],
      availableScans: [],
      isAddDialogVisible: false,
      addSelection: [],   // scanIds checked in the add dialog
      error: null,
      selectedScanId: null,
      gizmoMode: 'translate',  // 'translate' | 'rotate' | 'scale'
      dimOthers: true,
      dimOpacity: 0.35,
      // Numeric transform of the selected scan (rotation in degrees for display).
      xform: { px: 0, py: 0, pz: 0, rx: 0, ry: 0, rz: 0, sx: 1, sy: 1, sz: 1 },
      // Uniform scale for the primary (base) scan — sizes the whole version. Hydrated from the
      // primary's stored transform on load; render-only, so it never triggers a re-bake.
      primaryScale: 1,
      dirty: false,
      saving: false,
      savingView: false,
      publishing: false,
      isPublishConfirmVisible: false,
      viewMessage: null,   // transient snackbar text after saving the camera view
      loadState: {
        visible: true,
        phase: 'processing',  // 'download' | 'processing'
        progress: null,       // 0..1 | null (indeterminate)
        current: 0,
        total: 0,
        name: '',
      },
    };
  },
  computed: {
    downloadPercent() {
      return this.loadState.progress == null ? null : Math.round(this.loadState.progress * 100);
    },
    loadSubtitle() {
      if (!this.loadState.total) return 'Fetching scans…';
      return `${this.loadState.name} (${this.loadState.current} of ${this.loadState.total})`;
    },
    addableScans() {
      const inVersion = new Set(this.scanItems.map(i => i.scanId));
      return this.availableScans.filter(s => !inVersion.has(s.id));
    },
    selectedScanName() {
      return this.scanItems.find(i => i.scanId === this.selectedScanId)?.name ?? 'Scan';
    },
    // The currently selected scan's row — drives the per-scan Transform/Trim sections.
    selectedScanItem() {
      return this.scanItems.find(i => i.scanId === this.selectedScanId) ?? null;
    },
    selectedIsPrimary() {
      return !!this.selectedScanItem?.isPrimary;
    },
    // Log position of the base-scale slider (slider is exponent; factor = 10^exponent).
    primaryScaleLog() {
      return Math.log10(this.primaryScale || 1);
    },
    showViewMessage: {
      get() { return !!this.viewMessage; },
      set(v) { if (!v) this.viewMessage = null; },
    },
    // Publish button reflects the version's server-computed bake state.
    isBaking() {
      return !!this.version?.isBaking;
    },
    needsPublish() {
      return !!this.version?.needsPublish;
    },
    publishLabel() {
      if (this.isBaking) return 'Processing…';
      if (this.needsPublish) return 'Publish';
      return 'Published';
    },
    // Enabled only when there's something new to bake and a bake isn't already running.
    publishDisabled() {
      return this.publishing || this.saving || this.isBaking || !this.needsPublish;
    },
  },
  async mounted() {
    // Touch devices see the desktop-only notice instead — the canvas isn't rendered, so don't init.
    if (!this.isDesktopPointer) return;
    this.engine = new ViewerEngine();
    this.engine.mount(this.$refs.canvasContainer);
    this.engine.setOnChange(() => { this.dirty = true; this.syncTransformFields(); });
    // Scan selection is sidebar-only — intentionally not wiring engine.setOnScanClick so clicks
    // in the 3D viewport never select/deselect a scan.

    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('beforeunload', this.onBeforeUnload);

    await this.loadVersionAndScans();
  },
  beforeUnmount() {
    // Tear down any live scrub drag first, so its window listeners can't fire against a disposed engine
    // (and so the global ew-resize cursor isn't left stuck).
    this.onXformScrubEnd();
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    this.engine?.dispose();
    this.engine = null;
  },
  async beforeRouteLeave() {
    const { confirm } = useConfirm();
    // Tier 1: genuinely unsaved edits — the strong warning.
    if (this.dirty) {
      return await confirm({
        title: 'Leave without saving?',
        message: 'You have unsaved changes that will be lost.',
        confirmText: 'Leave', cancelText: 'Stay', danger: true, icon: 'mdi-alert-outline',
      });
    }
    // Tier 2: changes are saved but not yet published — viewers still see the previous version.
    // Suppressed once a bake is in flight (isBaking): the user has already hit Publish, the bake runs
    // server-side regardless of navigation, and needsPublish stays true until it completes — so without
    // this guard they'd be warned about something they've already actioned.
    if (this.needsPublish && !this.isBaking) {
      return await confirm({
        title: 'Leave without publishing?',
        message: 'Your changes are saved but not published — viewers will see the previous version until you publish.',
        confirmText: 'Leave', cancelText: 'Stay', icon: 'mdi-publish',
      });
    }
    return true;
  },
  methods: {
    // Breadcrumb navigation (mirrors the runtime viewer).
    goToAssets() {
      this.$router.push({ name: 'Assets' });
    },
    goToAdmin() {
      this.$router.push({ name: 'AssetAdmin', params: { id: this.id } });
    },
    async loadVersionAndScans() {
      try {
        const versions = await ModelVersionService.getModelVersionsByAssetId(this.id);
        this.version = versions.find(v => v.id === this.versionId) ?? null;

        if (!this.version) {
          this.error = 'Model version not found.';
          this.loadState.visible = false;
          return;
        }

        let scans = this.version.scans ?? [];
        if (!scans.length && this.version.associatedScan && this.version.modelPath) {
          scans = [{ scanId: this.version.associatedScan, modelPath: this.version.modelPath, transform: null }];
        }
        if (!scans.length) {
          this.error = 'This version has no scans to display.';
          this.loadState.visible = false;
          return;
        }

        // Hydrate the base scan's uniform scale from its stored transform (render-only; 0/absent = 1).
        const primaryStoredScale = scans.find(s => s.scanId === this.version.associatedScan)?.transform?.scale?.x;
        this.primaryScale = (Number.isFinite(primaryStoredScale) && primaryStoredScale > 0) ? primaryStoredScale : 1;

        // One call gives names (any member, any status) + the add list (success only).
        const allScans = await ModelVersionService.getScansByAssetId(this.id);
        const nameById = new Map(allScans.map(s => [s.id, s.name]));
        this.availableScans = allScans.filter(s => s.status === SCAN_PROCESSING_SUCCESS);

        this.scanItems = scans.map((scan, i) => ({
          scanId: scan.scanId,
          name: nameById.get(scan.scanId) ?? 'Unknown scan',
          isPrimary: scan.scanId === this.version.associatedScan,
          status: 'loading',
          visible: true,
          pathColor: CAMERA_PATH_COLORS[i % CAMERA_PATH_COLORS.length],
          modelPath: scan.modelPath,
          transform: scan.transform,
          // Camera-helper trim state: hydrate any persisted radius from the API. trimMax is
          // recomputed once the PLY is loaded (it's derived from the scan's bounding box).
          trimRadius: Number(scan.trimRadius) || 0,
          trimMax: 10,
          helperCount: 0,
          // Live point-reduction readout, populated once the scan's geometry is ready.
          pointsTotal: 0,
          pointsVisible: 0,
          pointsHidden: 0,
          trimReductionPct: 0,
          // Decimation: hydrate persisted % of points to remove (0 = off), same as trimRadius.
          decimateRemoved: Number(scan.decimation) || 0,
        }));

        await this.loadScansIntoScene();
        this.alignCamera();
      } catch (e) {
        this.error = 'Failed to load scans.';
        if (process.env.NODE_ENV === 'development') {
          console.error('ScanAlignmentEditor load failed', e);
        }
      } finally {
        this.loadState.visible = false;
      }
    },
    async loadScansIntoScene() {
      this.loadState.total = this.scanItems.length;
      for (let i = 0; i < this.scanItems.length; i++) {
        const item = this.scanItems[i];
        this.loadState.current = i + 1;
        this.loadState.name = item.name;
        await this.loadScanIntoScene(item);
      }
    },
    async loadScanIntoScene(item) {
      this.loadState.phase = 'processing';
      this.loadState.progress = null;
      try {
        // Blob paths are stored relative; sign into a SAS URL before fetching (same as the viewer).
        const signedPath = await organizationSasService.getSignedUrl(item.modelPath);

        // Camera helpers drive the trim feature. Fetched up-front so the engine can precompute the
        // per-vertex nearest-helper distance during the same load. Failure here is non-fatal — the
        // scan still loads, just without trim support.
        const cullCenters = await this.fetchCameraHelperPositions(item);
        item.helperCount = cullCenters.length;

        await this.engine.loadScan(
          { scanId: item.scanId, modelPath: signedPath, transform: item.transform, cullCenters, pathColor: item.pathColor },
          (phase, value) => {
            if (phase === 'download') {
              this.loadState.phase = 'download';
              this.loadState.progress = value;
            } else if (phase === 'processing') {
              this.loadState.phase = 'processing';
            }
          },
        );

        // Slider max scales to the scan's actual extent so the control isn't all-or-nothing.
        const size = this.engine.getScanBoundsSize(item.scanId);
        item.trimMax = size > 0 ? Math.max(1, Math.round(size * 10) / 10) : 10;

        // Apply any persisted trim now that the cull distances are attached and the shader is ready.
        if (item.trimRadius > 0) {
          this.engine.setScanTrimRadius(item.scanId, item.trimRadius);
        }
        // Apply any persisted decimation (stored as % removed → shader keep-fraction).
        if (item.decimateRemoved > 0) {
          this.engine.setScanDecimation(item.scanId, 1 - item.decimateRemoved / 100);
        }
        this.refreshTrimCounts(item);

        item.status = 'loaded';
      } catch (e) {
        item.status = 'error';
        if (process.env.NODE_ENV === 'development') {
          console.error(`Failed to load scan ${item.scanId}`, e);
        }
      }
    },
    async fetchCameraHelperPositions(item) {
      try {
        const helpers = await AssetService.getCameraHelpersAsync(item.scanId);
        // Dedupe cubemap faces, then sort by imageName so the camera-path polyline traces the
        // actual capture sequence rather than table-storage insertion order.
        const deduped = dedupeCubemapHelpers(helpers ?? [])
          .sort((a, b) => (a.imageName ?? '').localeCompare(b.imageName ?? '', undefined, { numeric: true }));
        return helperPositions(deduped);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`Failed to load camera helpers for scan ${item.scanId} — trim disabled.`, e);
        }
        return [];
      }
    },
    // Number-box entry: clamp, write the clamped value straight back to the input so the box never
    // shows an out-of-range value the model rejected (the :value binding won't repaint on a no-op change).
    onTrimNumberChange(item, e) {
      const clamped = Math.min(item.trimMax || Infinity, Math.max(0, Number(e.target.value) || 0));
      e.target.value = clamped.toFixed(2);
      this.onTrimRadiusChanged(item, clamped);
    },
    onDecimationNumberChange(item, e) {
      const clamped = Math.min(70, Math.max(0, Math.round(Number(e.target.value) || 0)));
      e.target.value = String(clamped);
      this.onDecimationChanged(item, clamped);
    },
    onPrimaryScaleNumberChange(e) {
      const clamped = Math.min(100000, Math.max(0.001, Number(e.target.value) || 1));
      e.target.value = String(clamped);
      this.onPrimaryScaleChanged(clamped);
    },
    onTrimRadiusChanged(item, value) {
      const radius = Math.min(item.trimMax || Infinity, Math.max(0, Number(value) || 0));
      if (item.trimRadius === radius) return;
      item.trimRadius = radius;
      this.engine.setScanTrimRadius(item.scanId, radius);
      this.refreshTrimCounts(item);
      this.dirty = true;
    },
    // Pull the visible/hidden point counts for the item's current trim radius AND decimation level
    // from the engine (O(1) lookup — safe to call on every slider tick). Shows the combined result.
    refreshTrimCounts(item) {
      const keep = 1 - (Number(item.decimateRemoved) || 0) / 100;
      const { total, visible, hidden, pct } = this.engine.getTrimCounts(item.scanId, item.trimRadius, keep);
      item.pointsTotal = total;
      item.pointsVisible = visible;
      item.pointsHidden = hidden;
      item.trimReductionPct = pct;
    },
    onDecimationChanged(item, value) {
      const removed = Math.min(70, Math.max(0, Math.round(Number(value) || 0)));
      if (item.decimateRemoved === removed) return;
      item.decimateRemoved = removed;
      this.engine.setScanDecimation(item.scanId, 1 - removed / 100);
      this.refreshTrimCounts(item);
      this.dirty = true;
    },
    resetDecimation(item) {
      this.onDecimationChanged(item, 0);
    },
    formatCount(n) {
      return (Number(n) || 0).toLocaleString();
    },
    resetTrimRadius(item) {
      this.onTrimRadiusChanged(item, 0);
    },
    alignCamera() {
      const mv = this.version;
      const applied = this.engine.applyCameraViewpoint({
        position: { x: mv.modelViewPointX, y: mv.modelViewPointY, z: mv.modelViewPointZ },
        rotation: { x: mv.modelViewPointRotationX, y: mv.modelViewPointRotationY, z: mv.modelViewPointRotationZ },
      });
      if (!applied) this.engine.frameScan(mv.associatedScan);
    },
    selectScan(item) {
      if (item.status !== 'loaded') return;

      if (this.selectedScanId === item.scanId) {
        this.deselect();
        return;
      }

      this.selectedScanId = item.scanId;
      // The primary is the fixed reference frame: select it to reveal its scan-specific actions
      // (trim) but never attach the move gizmo — only supplementary scans can be transformed.
      this.engine.selectScan(item.scanId, !item.isPrimary);

      if (!item.isPrimary) {
        // Snapshot the pose at selection so "Cancel move" can revert exactly this edit.
        this.editSnapshot = this.engine.getScanTransform(item.scanId);
        this.syncTransformFields();
      } else {
        this.editSnapshot = null;
      }
    },
    deselect() {
      this.selectedScanId = null;
      this.editSnapshot = null;
      this.engine.deselectScan();
    },
    cancelMove() {
      if (this.selectedScanId && this.editSnapshot) {
        this.engine.setScanTransform(this.selectedScanId, this.editSnapshot);
      }
      this.deselect();
    },
    toggleVisible(item) {
      item.visible = !item.visible;
      this.engine.setScanVisible(item.scanId, item.visible);
      if (!item.visible && this.selectedScanId === item.scanId) this.deselect();
    },
    syncTransformFields() {
      if (!this.selectedScanId) return;
      const t = this.engine.getScanTransform(this.selectedScanId);
      if (!t) return;
      const deg = r => Math.round((r * 180 / Math.PI) * 100) / 100;  // 2 dp
      const round = n => Math.round(n * 1000) / 1000;
      this.xform = {
        px: round(t.position.x), py: round(t.position.y), pz: round(t.position.z),
        rx: deg(t.rotation.x), ry: deg(t.rotation.y), rz: deg(t.rotation.z),
        sx: round(t.scale.x), sy: round(t.scale.y), sz: round(t.scale.z),
      };
    },
    // Scrub a transform field by dragging it horizontally — drag far for large moves, hold Shift for
    // fine. A plain click (no drag) still focuses the input for exact typing. Per-axis sensitivity is
    // matched to the field type (metres / degrees / factor).
    startXformScrub(e, key) {
      if (e.button !== 0) return;
      const sens = {
        p: { step: 0.02, fine: 0.004 },   // position (m per px)
        r: { step: 0.5, fine: 0.1 },      // rotation (° per px)
        s: { step: 0.01, fine: 0.002 },   // scale (× per px)
      }[key[0]];
      this._scrub = {
        key, startX: e.clientX, startVal: Number(this.xform[key]) || 0,
        step: sens.step, fine: sens.fine, moved: false, el: e.currentTarget,
      };
      window.addEventListener('pointermove', this.onXformScrubMove);
      window.addEventListener('pointerup', this.onXformScrubEnd);
    },
    onXformScrubMove(e) {
      const s = this._scrub;
      if (!s || !this.engine) return;
      const dx = e.clientX - s.startX;
      if (!s.moved) {
        if (Math.abs(dx) < 3) return;     // below threshold → still a click (keep caret for typing)
        s.moved = true;
        s.el.blur?.();                    // leave typing mode once a drag starts
        document.body.style.cursor = 'ew-resize';
      }
      const per = e.shiftKey ? s.fine : s.step;
      this.xform[s.key] = Math.round((s.startVal + dx * per) * 1000) / 1000;
      this.applyTransformFields();
    },
    onXformScrubEnd() {
      window.removeEventListener('pointermove', this.onXformScrubMove);
      window.removeEventListener('pointerup', this.onXformScrubEnd);
      document.body.style.cursor = '';
      this._scrub = null;
    },
    // Reset the selected scan back to identity (no offset / rotation / 1× scale).
    resetSelectedTransform() {
      if (!this.selectedScanId) return;
      this.xform = { px: 0, py: 0, pz: 0, rx: 0, ry: 0, rz: 0, sx: 1, sy: 1, sz: 1 };
      this.applyTransformFields();
    },
    applyTransformFields() {
      if (!this.selectedScanId) return;
      const f = this.xform;
      const rad = d => (Number(d) || 0) * Math.PI / 180;
      const num = (n, fallback = 0) => (Number.isFinite(Number(n)) ? Number(n) : fallback);
      this.engine.setScanTransform(this.selectedScanId, {
        position: { x: num(f.px), y: num(f.py), z: num(f.pz) },
        rotation: { isEuler: true, x: rad(f.rx), y: rad(f.ry), z: rad(f.rz), order: 'XYZ' },
        scale: { x: num(f.sx, 1), y: num(f.sy, 1), z: num(f.sz, 1) },
      });
      this.dirty = true;
    },
    // Slider position (log10 exponent) → actual scale factor.
    scaleFromLog(logValue) {
      return Math.pow(10, Number(logValue) || 0);
    },
    // Applies a uniform scale to the base scan (identity position/rotation preserved). The primary
    // has no gizmo, so this drives it directly and flags the version dirty.
    onPrimaryScaleChanged(value) {
      // Wide range: SfM/photogrammetry clouds can come out 100s× too small (or large), so allow
      // 0.001×–100000×. The slider is log-scaled; the number box takes an exact factor.
      const s = Math.min(100000, Math.max(0.001, Number(value) || 1));
      this.primaryScale = s;
      const primaryId = this.version?.associatedScan;
      if (primaryId) this.engine.setScanTransform(primaryId, { scale: { x: s, y: s, z: s } });
      this.dirty = true;
    },
    setGizmoMode(mode) {
      this.gizmoMode = mode;
      this.engine.setGizmoMode(mode);
    },
    toggleDimOthers() {
      this.dimOthers = !this.dimOthers;
      this.engine.setDimEnabled(this.dimOthers);
    },
    // Saves the current camera pose as this version's default viewpoint (the angle the runtime
    // viewer opens at). Moved here from the runtime viewer's admin toolbar — it's a version setting.
    async saveCameraView() {
      this.savingView = true;
      try {
        const pose = this.engine.getCameraPose();
        const model = new UpdateModelVersionModel(this.versionId, this.id, pose);
        const result = await ModelVersionService.updateModelVersion(model);
        this.viewMessage = (result && result.id) ? 'Default view saved' : 'Could not save view';
        // Keep the local copy in sync so re-entering the editor frames here.
        if (result && result.id) {
          this.version.modelViewPointX = pose.positionX;
          this.version.modelViewPointY = pose.positionY;
          this.version.modelViewPointZ = pose.positionZ;
          this.version.modelViewPointRotationX = pose.rotationX;
          this.version.modelViewPointRotationY = pose.rotationY;
          this.version.modelViewPointRotationZ = pose.rotationZ;
        }
      } catch (e) {
        this.viewMessage = 'Could not save view';
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to save camera view', e);
        }
      } finally {
        this.savingView = false;
      }
    },
    onDimOpacity(e) {
      this.dimOpacity = Number(e.target.value);
      this.engine.setDimOpacity(this.dimOpacity);
    },
    openAddDialog() {
      if (!this.addableScans.length) return;
      this.addSelection = [];
      this.isAddDialogVisible = true;
    },
    closeAddDialog() {
      this.isAddDialogVisible = false;
      this.addSelection = [];
    },
    async confirmAddScans() {
      // Ignore anything already in the version (defensive — addableScans already excludes them).
      const ids = this.addSelection.filter(id => !this.scanItems.some(i => i.scanId === id));
      this.closeAddDialog();
      if (!ids.length) return;

      const base = this.scanItems.length;
      const newItems = ids.map((scanId, j) => {
        const record = this.availableScans.find(s => s.id === scanId);
        return {
          scanId,
          name: record?.name ?? 'Unknown scan',
          isPrimary: false,
          status: 'loading',
          visible: true,
          pathColor: CAMERA_PATH_COLORS[(base + j) % CAMERA_PATH_COLORS.length],
          // Mirrors the API's ScanAssociationViewModel path; a newly added scan starts at identity.
          modelPath: `/${this.id}/scans/${scanId}/model/${scanId}.ply`,
          transform: null,
          trimRadius: 0,
          trimMax: 10,
          helperCount: 0,
          pointsTotal: 0,
          pointsVisible: 0,
          pointsHidden: 0,
          trimReductionPct: 0,
          decimateRemoved: 0,
        };
      });
      this.scanItems.push(...newItems);
      this.dirty = true;

      this.loadState.total = newItems.length;
      this.loadState.visible = true;
      for (let i = 0; i < newItems.length; i++) {
        this.loadState.current = i + 1;
        this.loadState.name = newItems[i].name;
        await this.loadScanIntoScene(newItems[i]);
      }
      this.loadState.visible = false;
    },
    onRemoveScan(item) {
      if (item.isPrimary) return;
      this.engine.removeScan(item.scanId);
      if (this.selectedScanId === item.scanId) this.deselect();
      this.scanItems = this.scanItems.filter(i => i.scanId !== item.scanId);
      this.dirty = true;
    },
    async saveTransforms() {
      this.saving = true;
      try {
        const primaryId = this.version.associatedScan;
        const supplementaryScans = this.scanItems
          .filter(i => !i.isPrimary)
          .map(i => ({
            scanId: i.scanId,
            transform: this.engine.getScanTransform(i.scanId) ?? i.transform,
            trimRadius: i.trimRadius,
            decimation: i.decimateRemoved,
          }));

        const primaryItem = this.scanItems.find(i => i.isPrimary);
        const primaryScanTrimRadius = primaryItem?.trimRadius ?? 0;
        const primaryScanDecimationValue = primaryItem?.decimateRemoved ?? 0;

        const model = new UpdateModelVersionScansModel(
          this.versionId, this.id, primaryId, supplementaryScans, primaryScanTrimRadius, primaryScanDecimationValue,
          this.primaryScale,
        );
        const updated = await ModelVersionService.updateModelVersionScans(model);

        // Sync each item's stored transform to what was just saved, so Cancel baselines move.
        for (const s of supplementaryScans) {
          const item = this.scanItems.find(i => i.scanId === s.scanId);
          if (item) item.transform = s.transform;
        }
        // Refresh bake state from the server so the Publish button reflects the just-saved params
        // (otherwise needsPublish stays at its load-time value and Publish never enables in-session).
        if (updated) {
          this.version.needsPublish = updated.needsPublish;
          this.version.isBaking = updated.isBaking;
          if (updated.scans?.length) this.version.scans = updated.scans;
        }
        this.dirty = false;
        // Drop the selection so the gizmo clears and the whole model is visible again.
        this.deselect();
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to save scan transforms', e);
        }
      } finally {
        this.saving = false;
      }
    },
    // Publish flow: save any pending edits first (so the bake reflects what's on screen), then
    // confirm. The actual publish call happens in confirmPublish once the user accepts.
    async onPublishClicked() {
      if (this.publishDisabled) return;
      if (this.dirty) {
        await this.saveTransforms();
        if (this.dirty) return;  // save failed — don't proceed to publish
      }
      this.isPublishConfirmVisible = true;
    },
    async confirmPublish() {
      this.publishing = true;
      try {
        const orgId = this.organizationStore.organizationId;
        const updated = await ModelVersionService.publishModelVersion(orgId, this.id, this.versionId);
        // Refresh bake state (needsPublish / isBaking) from the server's response.
        if (updated) {
          this.version.needsPublish = updated.needsPublish;
          this.version.isBaking = updated.isBaking;
          if (updated.scans?.length) this.version.scans = updated.scans;
        }
        this.viewMessage = 'Publishing started — optimised files are baking in the background.';
      } catch (e) {
        this.viewMessage = 'Could not start publishing.';
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to publish model version', e);
        }
      } finally {
        this.publishing = false;
      }
    },
    onKeyDown(e) {
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;

      if (e.key === 'Escape') { this.deselect(); return; }
      if (!this.selectedScanId) return;

      const key = e.key.toLowerCase();
      if (key === 'w') this.setGizmoMode('translate');
      else if (key === 'e') this.setGizmoMode('rotate');
      else if (key === 'r') this.setGizmoMode('scale');
    },
    onBeforeUnload(e) {
      // Warn on a hard reload/close for unsaved edits OR saved-but-unpublished changes. Browsers
      // show their own generic message; returnValue just needs to be a non-empty string to trigger it.
      if (this.dirty || (this.needsPublish && !this.isBaking)) {
        e.preventDefault();
        e.returnValue = this.dirty
          ? 'You have unsaved changes.'
          : 'Your changes are saved but not published.';
      }
    },
  },
};
</script>

<style scoped>
/* ----- Full-screen stage (mirrors ViewAsset) ----- */
/* Negative margins cancel the .scrollable-body padding so the viewer is edge-to-edge and reaches the
   top; v-main already offsets for the app sidebar, so no fixed positioning / top offset is needed. */
.viewer-container {
  position: relative;
  margin: -20px;
  width: auto;
  height: calc(100% + 40px);
  min-height: 0;
  padding: 0;
  max-width: none;
  background-color: #1a1f24;
  overflow: hidden;
}

.viewer-stage {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
}

.viewer-wrap {
  position: absolute;
  inset: 0;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  overflow: hidden;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.35);
}

.canvas-host {
  position: absolute;
  inset: 0;
}

/* ----- Breadcrumb pill (top-left, mirrors ViewAsset) ----- */
.viewer-crumb {
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 20;
  display: flex;
  align-items: center;
  height: 48px;
  padding: 0 16px;
  background: rgba(20, 30, 36, 0.82);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(6px);
  max-width: calc(100% - 320px - 48px);
}
.tb-crumbs { display: flex; align-items: center; gap: 6px; min-width: 0; }
.tb-crumb { background: none; border: none; padding: 0; font: inherit; font-size: 0.86rem; color: #8aa0aa; cursor: pointer; white-space: nowrap; }
.tb-crumb:hover:not(.current) { color: #cfd8dc; }
.tb-crumb.current { color: #fff; font-weight: 700; overflow: hidden; text-overflow: ellipsis; }
.tb-sep { color: #5c6f78; flex: 0 0 auto; }

/* ----- Floating control column (right-hand, mirrors the runtime viewer) ----- */
.floating-sidebar {
  position: absolute;
  top: 14px;
  bottom: 14px;
  right: 14px;
  width: 320px;
  z-index: 15;
  pointer-events: none;
}

/* One cohesive panel (mirrors the runtime viewer's single right-hand panel): the content is the card,
   and the sections inside it are divided by thin rules rather than being separate floating boxes. */
.sidebar-content {
  pointer-events: auto;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
}

/* Scrolls the sections while the footer (Save / Publish) stays pinned and always on screen. */
.sidebar-scroll {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
}

.sidebar-scroll::-webkit-scrollbar { width: 6px; }
.sidebar-scroll::-webkit-scrollbar-thumb { background: #3a4a52; border-radius: 3px; }
.sidebar-scroll::-webkit-scrollbar-thumb:hover { background: var(--duallo-accent-muted); }

/* Pinned footer — the Save button must never scroll off screen. */
.sidebar-footer {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

/* Compact "Save view" action in the Scans section header. */
.scans-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
.hdr-action {
  flex: 0 0 auto; display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 10px; border-radius: 8px; font: inherit; font-size: 0.78rem; font-weight: 600;
  background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.1); color: #cfd8dc; cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.hdr-action:hover:not(:disabled) { background: rgba(100, 255, 218, 0.1); border-color: rgba(100, 255, 218, 0.35); color: var(--duallo-accent); }
.hdr-action:disabled { opacity: 0.55; cursor: not-allowed; }

.card-header-text { min-width: 0; }

/* ----- Sidebar sections — transparent within the one panel, divided by a thin rule ----- */
.sidebar-card {
  flex: 0 0 auto;
  color: #fff;
  display: flex;
  flex-direction: column;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}
.sidebar-card:first-child { border-top: none; }

.card-header {
  padding: 12px 14px 8px;
}

.card-eyebrow {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #8aa0aa;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-title {
  font-size: 0.95rem;
  font-weight: 700;
  margin: 2px 0 0;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 8px;
}

.title-count {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--duallo-accent);
}

.card-body {
  padding: 4px 14px 14px;
}

/* ----- Add + scan list ----- */
.add-scan-activator {
  display: block;
  width: 100%;
  margin-bottom: 12px;
}

.add-scan-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  color: var(--duallo-accent) !important;
}

/* ----- Add-scan dialog ----- */
.add-dialog {
  background: var(--duallo-card-gradient) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 14px !important;
  color: #fff;
}

.add-dialog-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #fff;
  padding: 16px 18px 6px;
}

.add-dialog-body { padding: 0 18px 8px; }

.add-dialog-hint {
  font-size: 0.85rem;
  color: #8aa0aa;
  margin: 0 0 12px;
  line-height: 1.4;
}

.add-scan-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 320px;
  overflow-y: auto;
}

.add-scan-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 10px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.add-scan-option:hover { background: rgba(255, 255, 255, 0.05); }
.add-scan-option input { accent-color: var(--duallo-accent); cursor: pointer; }
.add-scan-option-icon { color: #8aa0aa; flex: 0 0 auto; }

.add-scan-option-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #fff;
  font-weight: 600;
  font-size: 0.9rem;
}

.add-dialog-actions { padding: 8px 18px 16px; gap: 8px; }

.add-dialog .ghost-btn {
  padding: 8px 16px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #cfd8dc;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.add-dialog .ghost-btn:hover { background: rgba(255, 255, 255, 0.08); }

.add-dialog .primary-btn {
  padding: 8px 16px;
  border-radius: 10px;
  background: rgba(100, 255, 218, 0.14);
  border: 1px solid rgba(100, 255, 218, 0.4);
  color: var(--duallo-accent);
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.add-dialog .primary-btn:hover { background: rgba(100, 255, 218, 0.22); }
.add-dialog .primary-btn:disabled { opacity: 0.5; cursor: default; }

.scan-rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 34vh;
  overflow-y: auto;
}

.scan-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 10px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid transparent;
  border-radius: 8px;
  font-size: 0.88rem;
}

.scan-row.is-selectable { cursor: pointer; }
.scan-row.is-selectable:hover { background: rgba(255, 255, 255, 0.04); }
.scan-row.is-selected { border-color: var(--duallo-accent); background: rgba(100, 255, 218, 0.1); }

.scan-color-dot {
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.4);
}

.scan-icon { color: #8aa0aa; flex: 0 0 auto; }

.scan-row-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #fff;
  font-weight: 600;
}

.scan-status {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  width: 18px;
  justify-content: center;
}

.icon-btn { flex: 0 0 auto; color: #8aa0aa !important; }
.icon-btn:hover { color: #cfd8dc !important; }
.remove-btn:hover { color: #f44336 !important; }

/* ----- Toggles + slider ----- */
.row-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.84rem;
  color: #cfd8dc;
  cursor: pointer;
}

.row-toggle input { accent-color: var(--duallo-accent); cursor: pointer; }

.dim-toggle { margin-top: 12px; }

.slider-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}

.slider-label { font-size: 0.8rem; color: #8aa0aa; }
.slider-row input[type="range"] { flex: 1 1 auto; accent-color: var(--duallo-accent); }
.slider-value { font-size: 0.8rem; color: #cfd8dc; width: 36px; text-align: right; font-variant-numeric: tabular-nums; }

/* ----- Trim by camera path ----- */
.trim-hint {
  font-size: 0.78rem;
  color: #8aa0aa;
  margin: 0 0 12px;
  line-height: 1.4;
}

.trim-row {
  padding: 10px 10px 8px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 8px;
}

.trim-row-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.trim-scan-name {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.trim-helper-count {
  font-size: 0.7rem;
  color: var(--duallo-accent-muted);
  font-variant-numeric: tabular-nums;
  flex: 0 0 auto;
}

.trim-helper-empty {
  font-size: 0.7rem;
  color: #6b7a82;
  font-style: italic;
  flex: 0 0 auto;
}

.trim-slider-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.trim-slider-row input[type="range"] {
  flex: 1 1 auto;
  accent-color: var(--duallo-accent);
}

.scale-number {
  width: 68px;
  flex: 0 0 auto;
  padding: 5px 6px;
  font-size: 0.8rem;
  color: #e0e6e9;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  font-variant-numeric: tabular-nums;
}

.scale-number:focus { outline: none; border-color: var(--duallo-accent); }

.scale-unit { font-size: 0.8rem; color: #8aa0aa; flex: 0 0 auto; }

.trim-counts {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 6px;
  font-size: 0.7rem;
  font-variant-numeric: tabular-nums;
  color: #8aa0aa;
}

.trim-count-hidden {
  color: #cfd8dc;
}

.trim-count-pct {
  margin-left: auto;
  color: var(--duallo-accent);
  font-weight: 500;
}

.trim-info {
  color: #8aa0aa;
  margin-left: 4px;
  vertical-align: -1px;
  cursor: help;
}

.trim-reset {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  color: #8aa0aa;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 0 auto;
  transition: color 0.15s ease, border-color 0.15s ease;
}

.trim-reset:hover {
  color: #cfd8dc;
  border-color: rgba(255, 255, 255, 0.18);
}


/* ----- Transform ----- */
.mode-toggle {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
}

.mode-toggle button {
  flex: 1 1 0;
  padding: 7px 0;
  font-size: 0.8rem;
  font-weight: 600;
  color: #b0bec5;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.mode-toggle button:hover { background: rgba(100, 255, 218, 0.08); }
.mode-toggle button.active { color: #04211c; background: var(--duallo-accent); border-color: var(--duallo-accent); }

.xform-grid {
  display: grid;
  grid-template-columns: auto 1fr 1fr 1fr;
  gap: 6px;
  align-items: center;
  margin-bottom: 8px;
}

.xform-axis { font-size: 0.72rem; color: #8aa0aa; font-weight: 600; }

.xform-grid input {
  width: 100%;
  min-width: 0;
  padding: 6px 6px;
  font-size: 0.8rem;
  color: #e0e6e9;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  font-variant-numeric: tabular-nums;
  /* Drag-to-scrub affordance; the caret still shows on click-to-type. */
  cursor: ew-resize;
}

.xform-grid input:focus { outline: none; border-color: var(--duallo-accent); cursor: text; }
/* Hide the native number spinners — scrubbing + typing + arrow keys cover adjustment. */
.xform-grid input::-webkit-outer-spin-button,
.xform-grid input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.xform-grid input[type="number"] { -moz-appearance: textfield; }

.xform-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
}
.xform-hint {
  display: inline-flex; align-items: center; gap: 4px;
  margin: 0; font-size: 0.68rem; color: #8aa0aa; line-height: 1.3;
}
.xform-reset {
  flex: 0 0 auto; display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 8px; border-radius: 6px; font: inherit; font-size: 0.72rem; font-weight: 600;
  background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.1); color: #cfd8dc; cursor: pointer;
}
.xform-reset:hover { background: rgba(100, 255, 218, 0.1); border-color: rgba(100, 255, 218, 0.35); color: var(--duallo-accent); }

/* Exact-entry number box beside the point-reduction sliders. */
.trim-number {
  width: 58px; flex: 0 0 auto;
  padding: 5px 6px; font-size: 0.78rem; color: #e0e6e9;
  background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px; font-variant-numeric: tabular-nums; text-align: right;
}
.trim-number:focus { outline: none; border-color: var(--duallo-accent); }
.trim-number::-webkit-outer-spin-button,
.trim-number::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.trim-number[type="number"] { -moz-appearance: textfield; }
.trim-unit { font-size: 0.75rem; color: #8aa0aa; flex: 0 0 auto; }

.cancel-btn {
  flex: 0 0 auto;
  margin-bottom: 4px;
  color: #ff8a80 !important;
  font-weight: 600 !important;
  text-transform: none !important;
}

.cancel-btn:hover { color: #ffb4ac !important; }

/* ----- Save / Publish ----- */
.footer-actions {
  display: flex;
  gap: 8px;
}

.footer-actions > .save-btn,
.footer-actions > .publish-btn {
  flex: 1 1 0;
  min-width: 0;
}

.save-btn {
  flex: 0 0 auto;
  margin-bottom: 4px;
  background-color: var(--duallo-accent) !important;
  color: #04211c !important;
  font-weight: 700 !important;
  text-transform: none !important;
}

.save-btn:disabled { opacity: 0.45; }

/* Publish sits beside Save as the secondary (outline) action — distinct from Save's solid teal. */
.publish-btn {
  margin-bottom: 4px;
  background-color: rgba(100, 255, 218, 0.12) !important;
  border: 1px solid rgba(100, 255, 218, 0.4) !important;
  color: var(--duallo-accent) !important;
  font-weight: 700 !important;
  text-transform: none !important;
}

.publish-btn:disabled { opacity: 0.45; }

/* ----- Loading overlay (mirrors ModelViewer) ----- */
.model-loading {
  position: absolute;
  inset: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(120% 90% at 50% 45%, rgba(26, 38, 45, 0.7) 0%, rgba(16, 22, 26, 0.82) 100%);
  backdrop-filter: blur(2px);
}

.ml-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  color: #fff;
  perspective: 320px;
}

.ml-percent { font-size: 1.1rem; font-weight: 700; color: #d6f8f1; font-variant-numeric: tabular-nums; }
.ml-title { font-size: 1.05rem; font-weight: 700; letter-spacing: 0.01em; }
.ml-sub { font-size: 0.85rem; color: #8aa0aa; margin-top: -8px; }

.ml-cube {
  position: relative;
  width: 58px;
  height: 58px;
  transform-style: preserve-3d;
  animation: ml-cube-spin 2.6s infinite ease-in-out;
  margin-bottom: 8px;
}

.ml-face {
  position: absolute;
  width: 58px;
  height: 58px;
  background: rgba(102, 199, 180, 0.16);
  border: 1.5px solid rgba(102, 199, 180, 0.85);
  box-shadow: inset 0 0 18px rgba(102, 199, 180, 0.25);
}

.ml-front  { transform: translateZ(29px); }
.ml-back   { transform: rotateY(180deg) translateZ(29px); }
.ml-right  { transform: rotateY(90deg) translateZ(29px); }
.ml-left   { transform: rotateY(-90deg) translateZ(29px); }
.ml-top    { transform: rotateX(90deg) translateZ(29px); }
.ml-bottom { transform: rotateX(-90deg) translateZ(29px); }

@keyframes ml-cube-spin {
  0%   { transform: rotateX(-24deg) rotateY(0deg); }
  100% { transform: rotateX(-24deg) rotateY(360deg); }
}
</style>
